import { useCallback, useMemo } from 'react';

import { LiveGame } from '@/features/v2/casino/interfaces/game.interface';

type LiveDataType = 'live' | 'highRollers';

interface CacheEntry {
  data: LiveGame[];
  timestamp: number;
  isLoading: boolean;
  error: string | null;
}

class LiveCache {
  private cache = new Map<string, CacheEntry>();
  private readonly CACHE_DURATION = 10 * 60 * 1000;
  private refreshTimeouts = new Map<string, NodeJS.Timeout>();

  set(key: string, data: LiveGame[], isLoading: boolean, error: string | null) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      isLoading,
      error,
    });
  }

  get(key: string): CacheEntry | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > this.CACHE_DURATION;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry;
  }

  scheduleRefresh(key: string, refreshCallback: () => void) {
    const existingTimeout = this.refreshTimeouts.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeout = setTimeout(() => {
      refreshCallback();
      this.refreshTimeouts.delete(key);
    }, this.CACHE_DURATION);

    this.refreshTimeouts.set(key, timeout);
  }

  clearTimeout(key: string) {
    const timeout = this.refreshTimeouts.get(key);
    if (timeout) {
      clearTimeout(timeout);
      this.refreshTimeouts.delete(key);
    }
  }

  clear() {
    this.refreshTimeouts.forEach(timeout => clearTimeout(timeout));
    this.refreshTimeouts.clear();
    this.cache.clear();
  }

  clearByType(type: LiveDataType) {
    Array.from(this.cache.keys()).forEach(key => {
      if (key.startsWith(type)) {
        this.clearTimeout(key)
        this.cache.delete(key)
      }
    })
  }

  }


const liveCache = new LiveCache();

export const useLiveCache = (type: LiveDataType, instanceId?: string) => {
  const cacheKey = useMemo(() => {
    return `${type}${instanceId ? `-${instanceId}` : ''}`;
  }, [type, instanceId]);

  const getCachedData = useCallback((): CacheEntry | null => {
    return liveCache.get(cacheKey);
  }, [cacheKey]);

  const setCachedData = useCallback((
    data: LiveGame[], 
    isLoading: boolean, 
    error: string | null
  ) => {
    liveCache.set(cacheKey, data, isLoading, error);
  }, [cacheKey]);

  const scheduleRefresh = useCallback((refreshCallback: () => void) => {
    liveCache.scheduleRefresh(cacheKey, refreshCallback);
  }, [cacheKey]);

  const clearCache = useCallback(() => {
    liveCache.clearByType(type);
  }, [type]);

  const clearTimeout = useCallback(() => {
    liveCache.clearTimeout(cacheKey);
  }, [cacheKey]);

  return {
    getCachedData,
    setCachedData,
    scheduleRefresh,
    clearCache,
    clearTimeout,
  };
};
